import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { topicSettingsServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { getTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { userMetadataServerProxy } from 'src/serverProxy/userMetadataServerProxy';
import { receiveEntity } from 'src/store/modules/entityService/operations';
import { useMainEntityId } from 'src/utils/Hooks/reduxSelectors';

const STATE_PROPERTY_NAME = 'mainEntity';

export const useRefreshTopicEntity = (mainEntity: { type: 'topic' | 'topicgroup' }) => {
  const entityId = useMainEntityId() as string;
  const dispatch = useDispatch();

  const handleOwner = useCallback(
    async (entity: any, ownerId: string | null) => {
      if (ownerId) {
        const userMetadata = await userMetadataServerProxy.getUserProfiles([ownerId]);
        entity.owner = userMetadata[ownerId];
      } else {
        entity.owner = null;
      }
      dispatch(receiveEntity(STATE_PROPERTY_NAME, entity));
    },
    [dispatch]
  );

  const refreshTopic = useCallback(async () => {
    const entity = await topicSettingsServerProxy.getTopic(entityId);
    const { ownedByUserId: ownerId, sharedWithEntityId } = entity;

    const updatedEntity = {
      ...entity,
      type: 'topic',
      id: entity.topicId,
      name: entity.topicName,
      ownership: getTopicOrTopicGroupType(sharedWithEntityId, ownerId)
    };

    if (updatedEntity.ownership === 'Standard') {
      const categoryData = await topicSettingsServerProxy.getTopicCategories(entityId);
      const taxonomyIds = categoryData?.records?.reduce(
        (acc, record) => {
          acc.categoryIds.add(record?.categoryId);
          acc.subcategoryIds.add(record?.subcategoryId);
          return acc;
        },
        {
          categoryIds: new Set(),
          subcategoryIds: new Set()
        }
      );

      updatedEntity.includedCategoryIds = Array.from(taxonomyIds.categoryIds);
      updatedEntity.includedSubCategoryIds = Array.from(taxonomyIds.subcategoryIds);
    }

    await handleOwner(updatedEntity, ownerId);
    return null;
  }, [entityId, handleOwner]);

  const refreshTopicGroup = useCallback(async () => {
    const entity = await topicSettingsServerProxy.getTopicGroup(entityId);
    const { ownedByUserId: ownerId, sharedWithEntityId } = entity;

    const updatedEntity = {
      ...entity,
      type: 'topicgroup',
      id: entity.topicGroupId,
      name: entity.topicGroupName,
      ownership: getTopicOrTopicGroupType(sharedWithEntityId, ownerId)
    };

    await handleOwner(updatedEntity, ownerId);
    return null;
  }, [entityId, handleOwner]);

  return mainEntity?.type === 'topic' ? refreshTopic : refreshTopicGroup;
};
